import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Package, Train, Bus, Storefront, Moped, Bluetooth } from "@phosphor-icons/react";
import { createRoot } from "react-dom/client";

// Fix leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const POINT_COLORS = {
  train_station: "#2563eb",
  bus_station: "#7c3aed",
  courier: "#D96C4A",
  shop: "#3E6656",
};

const POINT_TYPE_LABELS = {
  bus_station: "Estacao Autocarro",
  train_station: "Estacao Comboio",
  courier: "Estafeta",
  shop: "Papelaria / Quiosque",
};

function createCustomIcon(pointType) {
  const color = POINT_COLORS[pointType] || "#2D4A3E";
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="width:32px;height:32px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.2);display:flex;align-items:center;justify-content:center;">
      <svg width="16" height="16" viewBox="0 0 256 256" fill="white"><path d="M128,16a88.1,88.1,0,0,0-88,88c0,75.3,80,132.17,83.41,134.55a8,8,0,0,0,9.18,0C136,236.17,216,179.3,216,104A88.1,88.1,0,0,0,128,16Zm0,56a32,32,0,1,1-32,32A32,32,0,0,1,128,72Z"/></svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
}

function createBagIcon() {
  return L.divIcon({
    className: "bag-marker",
    html: `<div style="width:28px;height:28px;border-radius:50%;background:#D96C4A;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;animation:pulse-dot 2s infinite;">
      <svg width="14" height="14" viewBox="0 0 256 256" fill="white"><path d="M216,64H176a48,48,0,0,0-96,0H40A16,16,0,0,0,24,80V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V80A16,16,0,0,0,216,64ZM128,32a32,32,0,0,1,32,32H96A32,32,0,0,1,128,32Z"/></svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
}

function createVehicleIcon(vehicleType) {
  const colors = { van: "#1a5276", motorcycle: "#e67e22", bicycle: "#27ae60", bus: "#8e44ad", train: "#2c3e50" };
  const color = colors[vehicleType] || "#1a5276";
  return L.divIcon({
    className: "vehicle-marker",
    html: `<div style="width:34px;height:34px;border-radius:8px;background:${color};border:3px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
      <svg width="18" height="18" viewBox="0 0 256 256" fill="white"><path d="M240,112H229.2L201.42,49.5A16,16,0,0,0,186.8,40H69.2a16,16,0,0,0-14.62,9.5L26.8,112H16a8,8,0,0,0,0,16h8v80a16,16,0,0,0,16,16H64a16,16,0,0,0,16-16V192h96v16a16,16,0,0,0,16,16h24a16,16,0,0,0,16-16V128h8a8,8,0,0,0,0-16ZM69.2,56H186.8l24.89,56H44.31ZM80,160H56a8,8,0,0,1,0-16H80a8,8,0,0,1,0,16Zm120,0H176a8,8,0,0,1,0-16h24a8,8,0,0,1,0,16Z"/></svg>
    </div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    popupAnchor: [0, -34],
  });
}

function createBeaconIcon(batteryLevel) {
  const color = batteryLevel < 20 ? "#e74c3c" : "#2563eb";
  return L.divIcon({
    className: "beacon-marker",
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 6px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;">
      <svg width="10" height="10" viewBox="0 0 256 256" fill="white"><path d="M176,168H80a8,8,0,0,0-8,8v32a8,8,0,0,0,8,8h96a8,8,0,0,0,8-8V176A8,8,0,0,0,176,168Zm-16,32H96V184h64Zm45.66-85.66a8,8,0,0,1-11.32,0,72,72,0,0,0-101.68,0,8,8,0,0,1-11.32-11.32,88,88,0,0,1,124.32,0A8,8,0,0,1,205.66,114.34Z"/></svg>
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
    popupAnchor: [0, -22],
  });
}

export default function NetworkMap({ points, bags, shipments }) {
  const [selectedType, setSelectedType] = useState("all");
  const [wsMessages, setWsMessages] = useState([]);
  const [vehiclePositions, setVehiclePositions] = useState([]);
  const [beaconPositions, setBeaconPositions] = useState([]);
  const wsRef = useRef(null);

  // Fetch vehicle and beacon positions
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vehicles/positions`);
        const data = await res.json();
        setVehiclePositions(data.vehicles || []);
        setBeaconPositions(data.beacons || []);
      } catch (e) {}
    };
    fetchPositions();
    const interval = setInterval(fetchPositions, 10000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket connection
  useEffect(() => {
    const wsUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace("https://", "wss://").replace("http://", "ws://");
    try {
      const ws = new WebSocket(`${wsUrl}/api/ws/tracking`);
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setWsMessages(prev => [data, ...prev.slice(0, 9)]);
      };
      ws.onerror = () => {};
      wsRef.current = ws;
    } catch (e) {
      // WebSocket might not work in all environments
    }
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, []);

  const filteredPoints = selectedType === "all" ? points : points.filter(p => p.point_type === selectedType);

  // Create transit lines between shipment pickup and delivery points
  const transitLines = shipments
    .filter(s => s.status === "in_transit")
    .map(s => {
      const pickup = points.find(p => p.name === s.pickup_point);
      const delivery = points.find(p => p.name === s.delivery_point);
      if (pickup && delivery) {
        return { positions: [[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]], shipment: s };
      }
      return null;
    })
    .filter(Boolean);

  // Bags with locations matching points
  const bagsOnMap = bags
    .filter(b => b.status === "in_transit" || b.status === "at_collection_point")
    .map(b => {
      const point = points.find(p => b.current_location.toLowerCase().includes(p.name.toLowerCase().split(" ")[0]));
      if (point) {
        return { ...b, lat: point.lat + (Math.random() - 0.5) * 0.005, lng: point.lng + (Math.random() - 0.5) * 0.005 };
      }
      return null;
    })
    .filter(Boolean);

  return (
    <div data-testid="network-map" className="pt-20 px-6 pb-12 max-w-7xl mx-auto">
      <div className="mb-6">
        <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 4 }}>Mapa da rede</span>
        <h1 style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700 }}>Mapa Interativo BluMove</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Pontos de recolha, entrega e bolsas em tempo real</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="map-filter">
        {[
          { id: "all", label: "Todos", color: "var(--primary)" },
          { id: "train_station", label: "Comboio", color: "#2563eb" },
          { id: "bus_station", label: "Autocarro", color: "#7c3aed" },
          { id: "courier", label: "Estafeta", color: "#D96C4A" },
          { id: "shop", label: "Lojas", color: "#3E6656" },
        ].map(f => (
          <button
            key={f.id}
            data-testid={`map-filter-${f.id}`}
            onClick={() => setSelectedType(f.id)}
            className="nav-item"
            style={{ border: "none", fontFamily: "Manrope", background: selectedType === f.id ? f.color : "transparent", color: selectedType === f.id ? "white" : "var(--text-muted)", fontSize: "0.8rem", padding: "6px 14px" }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Map */}
        <div className="lg:col-span-8">
          <div className="bento-item overflow-hidden" style={{ height: 520 }}>
            <MapContainer
              center={[39.5, -8.0]}
              zoom={7}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {filteredPoints.map(point => (
                <Marker key={point.id} position={[point.lat, point.lng]} icon={createCustomIcon(point.point_type)}>
                  <Popup>
                    <div style={{ fontFamily: "Manrope", minWidth: 180 }}>
                      <strong style={{ fontFamily: "Outfit", fontSize: "0.95rem" }}>{point.name}</strong>
                      <br />
                      <span style={{ fontSize: "0.75rem", color: "#666" }}>{POINT_TYPE_LABELS[point.point_type]}</span>
                      <br />
                      <span style={{ fontSize: "0.75rem" }}>{point.address}, {point.city}</span>
                      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                        <span style={{ fontSize: "0.7rem", background: "rgba(45,74,62,0.1)", padding: "2px 6px", borderRadius: 4 }}>{point.active_bags} bolsas</span>
                        <span style={{ fontSize: "0.7rem", background: "rgba(232,163,89,0.1)", padding: "2px 6px", borderRadius: 4 }}>{point.pending_pickups} recolhas</span>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {bagsOnMap.map(bag => (
                <Marker key={bag.id} position={[bag.lat, bag.lng]} icon={createBagIcon()}>
                  <Popup>
                    <div style={{ fontFamily: "Manrope" }}>
                      <strong style={{ fontFamily: "JetBrains Mono" }}>{bag.bag_code}</strong>
                      <br />
                      <span style={{ fontSize: "0.75rem" }}>{bag.status === "in_transit" ? "Em transito" : "No ponto"}</span>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {transitLines.map((line, i) => (
                <Polyline key={i} positions={line.positions} color="var(--accent)" weight={2} dashArray="8 8" opacity={0.7} />
              ))}
              {/* Vehicle markers */}
              {vehiclePositions.filter(v => v.current_lat && v.current_lng).map(v => (
                <Marker key={`v-${v.id}`} position={[v.current_lat, v.current_lng]} icon={createVehicleIcon(v.vehicle_type)}>
                  <Popup>
                    <div style={{ fontFamily: "Manrope", minWidth: 160 }}>
                      <strong style={{ fontFamily: "JetBrains Mono", fontSize: "0.9rem" }}>{v.plate}</strong>
                      <br />
                      <span style={{ fontSize: "0.75rem", color: "#666" }}>{v.brand} {v.model}</span>
                      {v.bags_on_board && v.bags_on_board.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <span style={{ fontSize: "0.65rem", fontWeight: 600, color: "#666" }}>Bolsas: </span>
                          {v.bags_on_board.map(b => (
                            <span key={b} style={{ fontSize: "0.7rem", background: "rgba(37,99,235,0.1)", padding: "1px 5px", borderRadius: 4, marginRight: 3, fontFamily: "JetBrains Mono" }}>{b}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
              {/* Beacon markers */}
              {beaconPositions.filter(b => b.lat && b.lng).map(b => (
                <Marker key={`b-${b.bag_code}`} position={[b.lat, b.lng]} icon={createBeaconIcon(b.battery_level || 100)}>
                  <Popup>
                    <div style={{ fontFamily: "Manrope" }}>
                      <strong style={{ fontFamily: "JetBrains Mono", color: "#2563eb" }}>{b.bag_code}</strong>
                      <br />
                      <span style={{ fontSize: "0.75rem" }}>Bateria: {b.battery_level}% | {b.lock_status === "locked" ? "Trancada" : "Aberta"}</span>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Live feed */}
        <div className="lg:col-span-4">
          {/* Legend */}
          <div className="bento-item p-5 mb-4">
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 10 }}>Legenda</h3>
            <div className="flex flex-col gap-2">
              {Object.entries(POINT_COLORS).map(([type, color]) => (
                <div key={type} className="flex items-center gap-2">
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: color, border: "2px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                  <span style={{ fontSize: "0.8rem" }}>{POINT_TYPE_LABELS[type]}</span>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#D96C4A", border: "2px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                <span style={{ fontSize: "0.8rem" }}>Bolsa em transito</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 14, height: 14, borderRadius: 4, background: "#1a5276", border: "2px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                <span style={{ fontSize: "0.8rem" }}>Veiculo ({vehiclePositions.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#2563eb", border: "2px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                <span style={{ fontSize: "0.8rem" }}>Beacon ({beaconPositions.length})</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="bento-item p-5 mb-4">
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 10 }}>Resumo da rede</h3>
            <div className="grid grid-cols-2 gap-3">
              <div style={{ textAlign: "center", padding: 8, borderRadius: 8, background: "var(--bg)" }}>
                <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", color: "var(--primary)", display: "block" }}>{points.length}</span>
                <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Pontos</span>
              </div>
              <div style={{ textAlign: "center", padding: 8, borderRadius: 8, background: "var(--bg)" }}>
                <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", color: "var(--accent)", display: "block" }}>{bagsOnMap.length}</span>
                <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Bolsas ativas</span>
              </div>
            </div>
          </div>

          {/* Live updates */}
          <div className="bento-item p-5" data-testid="live-feed">
            <div className="flex items-center gap-2 mb-3">
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", animation: "pulse-dot 2s infinite" }} />
              <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem" }}>Atualizacoes em tempo real</h3>
            </div>
            <div className="flex flex-col gap-2" style={{ maxHeight: 240, overflowY: "auto" }}>
              {wsMessages.length > 0 ? wsMessages.map((msg, i) => (
                <div key={i} style={{ padding: "8px 10px", borderRadius: 8, background: "var(--bg)", fontSize: "0.75rem" }}>
                  <div className="flex items-center gap-2">
                    <Bluetooth size={12} style={{ color: "var(--primary)" }} />
                    <span style={{ fontFamily: "JetBrains Mono", fontWeight: 500, color: "var(--primary)" }}>{msg.bag_code}</span>
                  </div>
                  <span style={{ color: "var(--text-muted)" }}>{msg.location}</span>
                </div>
              )) : (
                <p style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>A aguardar atualizacoes dos beacons...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
